import { FileService, eFileService } from "../../../services/SubmissionService";
import { OrganizationService } from "../../../services/ClientService";
import * as APIModels from "../../../models";
import pdfIcon from "../../../assets/images/file-icons/pdf.svg";
import zipIcon from "../../../assets/images/file-icons/zip.svg";
import docIcon from "../../../assets/images/file-icons/doc.svg";
import { Endpoints } from "../../../helpers/Endpoints";
import { useRef, useState, useCallback } from "react";
import { useEffect } from "react";
import { formatBytes } from "../../../helpers/utility";
import { Globals } from "../../../helpers/globals";
import { useDropzone, DropzoneOptions } from "react-dropzone";
import {
    ErrorModal,
    ErrorModalProps,
} from "../../../components/shared/modals/ErrorModal";
import { authenticationService } from "../../../services/authentication.service";
import DeleteConfirmModal from "./modals/DeleteConfirmModal";
import { string32 } from "pdfjs-dist/types/src/shared/util";
import React from "react";
import { setRef } from "@material-ui/core";

export default function FileManager() {
    const f: APIModels.FileInfo[] = [];
    const [files, setFiles] = useState(f);
    const [organization, setOrganization] = useState(
        new APIModels.Organization()
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedFiles, setSelectedFiles] = useState(f);
    const [selectAllFiles, setSelectAllFiles] = useState(true);
    const iframeRef = useRef<HTMLEmbedElement>(null);
    const [refresh, setRefresh] = useState(false);
    const [hoverElement, setHoverElement] = useState(
        null as HTMLElement | null
    );
    const [show, setShow] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(new APIModels.FileInfo());
    const [filesToDelete, setFilesToDelete] = useState(
        [] as APIModels.FileInfo[]
    );

    const filenameRegex = /[^a-zA-Z0-9_-]+/g;

    const onCloseErrorModal = () => {
        setShow(false);
        setErrorMessage("");
        setErrorModalProps({
            onClose: onCloseErrorModal,
        } as ErrorModalProps);

        // refresh the page
        setRefresh(!refresh);
    };

    const [errorModalProps, setErrorModalProps] = useState({
        errorMessage: "",
        onClose: onCloseErrorModal,
    } as ErrorModalProps);

    const currentUser = Globals.currentUserValue;

    useEffect(() => {
        if (errorMessage !== "") {
            setErrorModalProps({
                ...errorModalProps,
                errorMessage: errorMessage,
            });
            setShow(true);
        } else {
            setShow(false);
        }
    }, [errorMessage]);

    useEffect(() => {
        OrganizationService.get({ id: currentUser.organizationID ?? "" }).then(
            (resp) => {
                if (resp.successful === true && resp.data) {
                    setOrganization(resp.data);
                }
            },
            (error) => {
                setErrorMessage(error.message);
            }
        );
    }, []);

    useEffect(() => {
        FileService.list({
            route: "uploads",
        }).then(
            (resp) => {
                if (resp.successful === true && resp.data) {
                    setFiles(resp.data);
                }
            },
            (error) => {
                setErrorMessage(error.message);
            }
        );
    }, [refresh]);

    const handleMouseOver = (
        event: React.MouseEvent<HTMLImageElement>,
        file: APIModels.FileInfo
    ) => {
        if (file.filename!.indexOf(".pdf") > 0) {
            setHoverElement(event.currentTarget);

            FileService.get({
                submissionId: currentUser.id + "/uploads",
                documentId: file.filename ?? "",
                source: "upload",
            }).then(
                (resp) => {
                    resp.json().then((json) => {
                        if (iframeRef && iframeRef.current)
                            iframeRef.current.src = json.url;
                    });
                },
                (error) => {
                    setErrorMessage(error.message);
                }
            );
        }
    };

    const onDrop = useCallback(
        (acceptedFiles) => {
            const fileInfo = [...files];
            acceptedFiles.forEach((file: any) => {
                // ensure that the file is a *.pdf or *.zip
                //    otherwise, display an error modal
                if (
                    !file.name.endsWith(".pdf") &&
                    !file.name.endsWith(".zip") &&
                    !file.name.endsWith(".docx")
                ) {
                    setErrorMessage(
                        "Invalid File Type. Please Upload a PDF or ZIP File or Docx"
                    );
                    return;
                }

                let sanatizedName = "";

                if (file.name.toLowerCase().includes(".zip")) {
                    sanatizedName =
                        file.name
                            .toLowerCase()
                            .split(".zip")[0]
                            .replace(filenameRegex, "") + ".zip";
                } else if (file.name.toLowerCase().includes(".docx")) {
                    sanatizedName =
                        file.name
                            .toLowerCase()
                            .split(".docx")[0]
                            .replace(filenameRegex, "") + ".docx";
                } else {
                    sanatizedName =
                        file.name
                            .toLowerCase()
                            .split(".pdf")[0]
                            .replace(filenameRegex, "") + ".pdf";
                }
                FileService.upload({
                    fileServiceUrl: Endpoints.FileManager ?? "",
                    file: file,
                    filename: "uploads/" + sanatizedName,
                }).then(
                    (resp) => {
                        setRefresh(!refresh);
                        fileInfo.push({ filename: file.name, size: file.size });
                        resp.text().then((text) => console.log(text));
                    },
                    (error) => {
                        setErrorMessage(error.message);
                    }
                );
            });

            setFiles(fileInfo);
        },
        [files]
    );

    const handleMouseOut = (event: React.MouseEvent<HTMLImageElement>) => {
        if (event.currentTarget == hoverElement) {
            setHoverElement(null);
        }
    };

    const onFileUpload = (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage("");

        const input = event.target as HTMLInputElement;

        if (!input.files?.length) {
            return;
        }
        const temp = [...files];
        let count = 0;
        const num = input.files.length;
        for (let i = 0; i < input.files.length; i++) {
            const f = input.files[i];
            if (f.name.includes(".zip") && !organization.batch) {
                setErrorMessage("Batch File Not Enabled For Account");
            } else {
                temp.push({ filename: f.name, size: f.size });
                setFiles(temp);

                if (
                    !f.name.toLowerCase().includes(".zip") &&
                    !f.name.toLowerCase().includes(".pdf") &&
                    !f.name.toLowerCase().endsWith(".docx")
                ) {
                    setErrorMessage(
                        "Invalid File Type. Please Upload a PDF or ZIP File or Docx"
                    );
                } else {
                    let sanatizedName = "";

                    if (f.name.toLowerCase().includes(".zip")) {
                        sanatizedName =
                            f.name
                                .toLowerCase()
                                .split(".zip")[0]
                                .replace(filenameRegex, "") + ".zip";
                    } else if (f.name.toLowerCase().includes(".docx")) {
                        sanatizedName =
                            f.name
                                .toLowerCase()
                                .split(".docx")[0]
                                .replace(filenameRegex, "") + ".docx";
                    } else {
                        sanatizedName =
                            f.name
                                .toLowerCase()
                                .split(".pdf")[0]
                                .replace(filenameRegex, "") + ".pdf";
                    }

                    FileService.upload({
                        fileServiceUrl: Endpoints.FileManager ?? "",
                        file: f,
                        filename: "uploads/" + sanatizedName,
                    }).then(
                        (resp) => {
                            count++;

                            if (count == num) {
                                setRefresh(!refresh);
                            }
                            input.value = "";
                        },
                        (error) => {
                            console.log("ERROR " + error);
                            input.value = "";
                        }
                    );
                }
            }
        }
    };
    const onConfirmDelete = useCallback(
        (filesToDelete) => {
            let count = 0;
            const num = filesToDelete.length;

            filesToDelete.forEach((file: any) => {
                FileService.delete({
                    filename: file.filename!,
                }).then(
                    (resp) => {
                        count++;
                        if (count == num) {
                            setShowDeleteModal(false);
                            setRefresh(!refresh);
                            setFilesToDelete([]);
                            setSelectedFiles([]);
                        }
                    },
                    (error) => {
                        console.log(error);
                    }
                );
            });
        },
        [refresh]
    );

    const hoverPreview = () => {
        const rect = hoverElement!.getBoundingClientRect();
        return (
            <div
                style={{
                    position: "fixed",
                    top: rect.top,
                    left: rect.left,
                    height: "800px",
                    width: "800px",
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            >
                <embed
                    ref={iframeRef}
                    type="application/pdf"
                    style={{ height: "800px", width: "800px" }}
                ></embed>
            </div>
        );
    };

    const onSelectFile = (index: number, file: APIModels.FileInfo) => {
        const matchingIndex = selectedFiles.findIndex(
            (v) => v.filename === file.filename
        );

        if (matchingIndex >= 0) {
            const filesUpdated = [...selectedFiles];
            filesUpdated.splice(matchingIndex, 1);
            setSelectedFiles(filesUpdated);
        } else {
            setSelectedFiles([...selectedFiles, file]);
        }

        return false;
    };

    const onSelectAllFiles = () => {
        if (selectAllFiles == true) {
            setSelectedFiles(files);
        } else {
            setSelectedFiles([]);
        }
        // setSelectedFiles([...selectedFiles,]);
    };

    const onEfile = (
        url: string,
        fileAsIndividuals: boolean,
        filename?: string,
        newCase?: boolean
    ) => {
        let filesToSubmit = [...selectedFiles];

        if (filename && selectedFiles.length == 0) {
            filesToSubmit = [{ filename }];
        }

        //make sure all of the files are pdfs or zips
        //or a doc/docx if this is an order
        const pdfs = filesToSubmit.filter(
            (d) =>
                d.filename?.toLowerCase().endsWith("pdf") ||
                d.filename?.toLowerCase().endsWith("zip")
        );

        if (
            pdfs.length != filesToSubmit.length &&
            url.toLowerCase().indexOf("order") < 0
        ) {
            setErrorMessage(
                "We are only able to accept files in PDF format for existing or new cases."
            );
        } else {
            console.log("Beging Efile " + url);
            eFileService
                .begin({
                    body: filesToSubmit.map((v) => v.filename),
                    fileAsIndividuals: fileAsIndividuals,
                })
                .then(
                    (resp) => {
                        console.log("RESPONSE " + JSON.stringify(resp));
                        if (resp.successful === true && resp.data) {
                            if (url.toLowerCase().indexOf("batch") > 0) {
                                window.location.href =
                                    url +
                                    "/" +
                                    (newCase === true ? "new/" : "existing/") +
                                    resp.data.id;
                            } else
                                window.location.href = url + "/" + resp.data.id;
                        } else setErrorMessage(resp?.errorMessage ?? "");
                    },
                    (error) => {
                        console.log("RESPONSE ");
                        setErrorMessage(error.message);
                    }
                );
        }
        // eFileService
        //     .begin({
        //         body: filesToSubmit.map((v) => v.filename),
        //         fileAsIndividuals: fileAsIndividuals,
        //     })
        //     .then(
        //         (resp) => {
        //             if (resp.successful === true && resp.data)
        //                 window.location.href = url + "/" + resp.data.id;
        //             else setErrorMessage(resp?.errorMessage ?? "");
        //         },
        //         (error) => {
        //             setErrorMessage(error.message);
        //         }
        //     );
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        maxSize: 35000000,
        accept: "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/zip, application/x-zip-compressed, multipart/x-zip",
        multiple: true,
    });

    const onDelete = (file: APIModels.FileInfo) => {
        if (selectedFiles.length == 0) {
            const temp = [...filesToDelete];
            temp.push(file);
            setFilesToDelete(temp);
        } else {
            setFilesToDelete([...selectedFiles]);
        }

        setShowDeleteModal(true);
    };

    return (
        <>
            {show && <ErrorModal {...errorModalProps} />}
            <div className="card-body">
                <div className="row">
                    <div className="col-sm-12">
                        <div
                            {...getRootProps({
                                className: "dropzone",
                            })}
                            style={{ border: "none" }}
                        >
                            <input {...getInputProps()} />
                            <div className="text-center">
                                <i className="h1 text-muted ri-upload-cloud-2-line">
                                    ...
                                </i>
                                <h3 className="text-center">
                                    Drop files here or click button to upload.
                                </h3>
                            </div>

                            <div className="fileupload btn btn-success waves-effect waves-light mb-3">
                                <span>
                                    <i className="mdi mdi-cloud-upload me-1"></i>{" "}
                                    Upload Files
                                </span>
                                <input
                                    type="file"
                                    className="upload"
                                    onChange={(event) => {
                                        onFileUpload(event);
                                        event.target.value = "";
                                    }}
                                    multiple
                                />
                            </div>
                        </div>
                    </div>
                    {organization.templateOnly === false ? (
                        <div className="col-sm-12 mb-3 text-end">
                            <button
                                className="btn btn-primary waves-effect waves-light mt-2"
                                type="button"
                                onClick={() =>
                                    onEfile("/efile/new-case", false)
                                }
                            >
                                Start A New Case
                            </button>
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
                <div className="row">
                    <div
                        className="table-responsive"
                        style={{ overflow: "visible" }}
                    >
                        {hoverElement && hoverPreview()}
                        <table
                            className="table table-centered  table-nowrap mb-0"
                            style={{ zIndex: 0 }}
                        >
                            <thead className="table-light">
                                <tr>
                                    <th scope="col">
                                        <input
                                            type="checkbox"
                                            style={{ marginRight: "10px" }}
                                            checked={!selectAllFiles}
                                            onChange={() => {
                                                setSelectAllFiles(
                                                    !selectAllFiles
                                                );
                                                onSelectAllFiles();
                                            }}
                                        />
                                        Select All
                                    </th>

                                    <th scope="col">File Name</th>
                                    <th scope="col">Date Modified</th>
                                    <th scope="col">Size</th>
                                    <th
                                        scope="col"
                                        className="text-center"
                                        style={{ width: 125 }}
                                    >
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.map((file, i) => {
                                    return (
                                        <tr>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        selectedFiles.filter(
                                                            (v) =>
                                                                v.filename ===
                                                                file.filename
                                                        ).length > 0
                                                    }
                                                    onChange={() =>
                                                        onSelectFile(i, file)
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <img
                                                    src={
                                                        file.filename &&
                                                        file.filename
                                                            .toLowerCase()
                                                            .endsWith(".pdf")
                                                            ? pdfIcon
                                                            : file
                                                                  .filename!.toLowerCase()
                                                                  .endsWith(
                                                                      ".zip"
                                                                  )
                                                            ? zipIcon
                                                            : docIcon
                                                    }
                                                    height="30"
                                                    alt="icon"
                                                    className="me-2"
                                                    onMouseEnter={(e) =>
                                                        handleMouseOver(e, file)
                                                    }
                                                    onMouseLeave={
                                                        handleMouseOut
                                                    }
                                                />
                                                <a
                                                    href="javascript:void(0);"
                                                    className="text-dark"
                                                >
                                                    {file.filename}
                                                </a>
                                            </td>
                                            <td className="font-13">
                                                {file.lastUpdatedDateTime}
                                            </td>
                                            <td>
                                                {formatBytes(file.size ?? 0)}
                                            </td>
                                            {file.lastUpdatedDateTime ? (
                                                <td>
                                                    {(selectedFiles.length ==
                                                        0 ||
                                                        selectedFiles.findIndex(
                                                            (v) =>
                                                                v.filename ===
                                                                file.filename
                                                        ) == 0) && (
                                                        <>
                                                            <div
                                                                className="btn-group dropdown"
                                                                style={{
                                                                    display:
                                                                        "inline-block",
                                                                }}
                                                            >
                                                                <a
                                                                    href="#"
                                                                    className="dropdown-toggle arrow-none btn btn-light btn-sm"
                                                                    data-bs-toggle="dropdown"
                                                                    aria-expanded="false"
                                                                >
                                                                    Click for
                                                                    eFile
                                                                    Options
                                                                </a>
                                                                <div
                                                                    className="dropdown-menu dropdown-menu-end"
                                                                    style={{
                                                                        zIndex: 1000,
                                                                    }}
                                                                >
                                                                    {file
                                                                        .filename!.toLowerCase()
                                                                        .endsWith(
                                                                            ".pdf"
                                                                        ) ? (
                                                                        <>
                                                                            {selectedFiles.length >
                                                                            1 ? (
                                                                                <>
                                                                                    <a
                                                                                        className="dropdown-item"
                                                                                        href="#"
                                                                                        onClick={() => {
                                                                                            onEfile(
                                                                                                "/efile/existing",
                                                                                                false,
                                                                                                file.filename
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <i className="mdi mdi-file-document me-2 text-muted font-18 vertical-middle"></i>
                                                                                        File
                                                                                        into
                                                                                        Existing
                                                                                        Case
                                                                                        Group
                                                                                    </a>
                                                                                    <a
                                                                                        className="dropdown-item"
                                                                                        href="#"
                                                                                        onClick={() =>
                                                                                            onEfile(
                                                                                                "/efile/existing",
                                                                                                true,
                                                                                                file.filename
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <i className="mdi mdi-file-document me-2 text-muted font-18 vertical-middle"></i>
                                                                                        File
                                                                                        into
                                                                                        Existing
                                                                                        Case
                                                                                        Individuals
                                                                                    </a>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    {" "}
                                                                                    <a
                                                                                        className="dropdown-item"
                                                                                        href="#"
                                                                                        onClick={() =>
                                                                                            onEfile(
                                                                                                "/efile/existing",
                                                                                                false,
                                                                                                file.filename
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <i className="mdi mdi-file-document me-2 text-muted font-18 vertical-middle"></i>
                                                                                        File
                                                                                        into
                                                                                        Existing
                                                                                        Case
                                                                                    </a>
                                                                                </>
                                                                            )}
                                                                            {organization.allowTemplates ===
                                                                            true ? (
                                                                                <a
                                                                                    className="dropdown-item"
                                                                                    href="#"
                                                                                    onClick={() => {
                                                                                        onEfile(
                                                                                            "/efile/template-new-case",
                                                                                            true,
                                                                                            file.filename
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <i className="mdi mdi-file-document me-2 text-muted font-18 vertical-middle"></i>
                                                                                    File
                                                                                    Template
                                                                                    as
                                                                                    New
                                                                                    Case
                                                                                </a>
                                                                            ) : (
                                                                                <>

                                                                                </>
                                                                            )}
                                                                            {organization.dfs ===
                                                                            false ? (
                                                                                <>

                                                                                </>
                                                                            ) : (
                                                                                <a
                                                                                    className="dropdown-item"
                                                                                    href="#"
                                                                                    onClick={() => {
                                                                                        onEfile(
                                                                                            "/efile/service",
                                                                                            true,
                                                                                            file.filename
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <i className="mdi mdi-file-document me-2 text-muted font-18 vertical-middle"></i>
                                                                                    Submit
                                                                                    for
                                                                                    Service
                                                                                </a>
                                                                            )}
                                                                        </>
                                                                    ) : file
                                                                          .filename!.toLowerCase()
                                                                          .endsWith(
                                                                              ".docx"
                                                                          ) ? (
                                                                        <>
                                                                            {" "}
                                                                            {organization.orders ===
                                                                            false ? (
                                                                                <>

                                                                                </>
                                                                            ) : (
                                                                                <a
                                                                                    className="dropdown-item"
                                                                                    //href={`/efile/order/${file.filename}`}
                                                                                    href="#"
                                                                                    onClick={() => {
                                                                                        onEfile(
                                                                                            "/efile/order",
                                                                                            true,
                                                                                            file.filename
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <i className="mdi mdi-file-document me-2 text-muted font-18 vertical-middle"></i>
                                                                                    File
                                                                                    as
                                                                                    Order
                                                                                </a>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {organization.batch ===
                                                                            false ? (
                                                                                <>

                                                                                </>
                                                                            ) : (
                                                                                <div>
                                                                                    <a
                                                                                        className="dropdown-item"
                                                                                        href="#"
                                                                                        onClick={() => {
                                                                                            onEfile(
                                                                                                "/efile/batch",
                                                                                                false,
                                                                                                file.filename,
                                                                                                true
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <i className="mdi mdi-check-all me-2 text-muted font-18 vertical-middle"></i>
                                                                                        File
                                                                                        Batch
                                                                                        as
                                                                                        New
                                                                                        Batch
                                                                                    </a>
                                                                                    <a
                                                                                        className="dropdown-item"
                                                                                        href="#"
                                                                                        onClick={() => {
                                                                                            onEfile(
                                                                                                "/efile/batch",
                                                                                                false,
                                                                                                file.filename,
                                                                                                false
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <i className="mdi mdi-check-all me-2 text-muted font-18 vertical-middle"></i>
                                                                                        File
                                                                                        Batch
                                                                                        as
                                                                                        Existing
                                                                                        Batch
                                                                                    </a>
                                                                                    <a
                                                                                        className="dropdown-item"
                                                                                        href="#"
                                                                                        onClick={() => {
                                                                                            onEfile(
                                                                                                "/efile/batch",
                                                                                                false,
                                                                                                file.filename,
                                                                                                false
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <i className="mdi mdi-check-all me-2 text-muted font-18 vertical-middle"></i>
                                                                                        File
                                                                                        Batch
                                                                                        as
                                                                                        Order
                                                                                        Batch
                                                                                    </a>
                                                                                </div>
                                                                            )}

                                                                            {organization.orders ===
                                                                            false ? (
                                                                                <>

                                                                                </>
                                                                            ) : (
                                                                                <a
                                                                                    className="dropdown-item"
                                                                                    //href={`/efile/order/${file.filename}`}
                                                                                    href="#"
                                                                                    onClick={() => {
                                                                                        onEfile(
                                                                                            "/efile/order",
                                                                                            true,
                                                                                            file.filename
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <i className="mdi mdi-file-document me-2 text-muted font-18 vertical-middle"></i>
                                                                                    File
                                                                                    as
                                                                                    Order
                                                                                </a>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <ul
                                                                className="list-inline table-action m-0"
                                                                style={{
                                                                    display:
                                                                        "inline-block",
                                                                }}
                                                            >
                                                                <li className="list-inline-item">
                                                                    <a
                                                                        href="javascript:void(0);"
                                                                        className="action-icon px-1"
                                                                        onClick={() => {
                                                                            onDelete(
                                                                                file
                                                                            );
                                                                        }}
                                                                    >
                                                                        {" "}
                                                                        <i className="mdi mdi-delete"></i>
                                                                    </a>
                                                                </li>
                                                            </ul>
                                                        </>
                                                    )}
                                                </td>
                                            ) : (
                                                <td>
                                                    <div
                                                        className="spinner-border text-primary m-2"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            Loading...
                                                        </span>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <DeleteConfirmModal
                            files={filesToDelete ?? []}
                            show={showDeleteModal}
                            onOk={() => onConfirmDelete(filesToDelete)}
                            onClose={() => {
                                setShowDeleteModal(false);
                                setFilesToDelete([]);
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
